import React, { useState, useEffect } from "react";
import Layout from "../../layout";
import { useSelector, useDispatch } from "react-redux";
import PrimaryHeading from "../../components/PrimaryHeading";
import { Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import {
  Box,
  Grid,
  Flex,
  Text,
  Input,
  Collapse,
  useDisclosure,
  Stack,
  Textarea,
} from "@chakra-ui/react";
import { FaEdit } from "react-icons/fa";
import CollapseIcon from "../../assets/images/collapse.svg";
import PrimaryFormLabel from "../../components/PrimaryFormLabel";
import PrimaryButton from "../../components/PrimaryButton";
import { PasswordField } from "./components/PasswordField";
import {
  fetchProfileDetailsRequest,
  sendOTPRequest,
  verifyUserOTPRequest,
} from "./authStore/actions";
import Alert from "../../components/Alert";
import OtpModal from "./components/OtpModal";
import PrimaryText from "../../components/PrimaryText";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { profileDetails, otpSent, userOtpResponse, currentPasswordMismatch } =
    useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isMessageOpen, setMessageOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState("Success!");
  const [alertType, setAlertType] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureImagePreview, setSignatureImagePreview] = useState(null);
  const [incorrectPasswordAlert, setIncorrectPasswordAlert] = useState(false);
  const [otpType, setOtpType] = useState("FP");
  const { isOpen: isSectionOpen, onToggle: toggleSection } = useDisclosure({
    defaultIsOpen: false,
  });
  const { isOpen: isSignatureSectionOpen, onToggle: toggleSignatureSection } =
    useDisclosure({
      defaultIsOpen: false,
    });

  useEffect(() => {
    if (otpSent) {
      onOpen();
    } else {
      onClose();
    }
  }, [otpSent]);

  useEffect(() => {
    if (currentPasswordMismatch) {
      setIncorrectPasswordAlert(true);
    }
  }, [currentPasswordMismatch]);

  useEffect(() => {
    setAlertTitle("Success!");
    setAlertType("success");
    if (userOtpResponse) {
      if (otpType === "FP") {
        setPassword("");
        setConfirmPassword("");
        setPasswordError("");
      }
      onClose();
      setShowAlert(true);
      setAlertMessage(userOtpResponse.message);
    } else {
      setShowAlert(false);
    }
  }, [userOtpResponse]);

  useEffect(() => {
    let payload = {
      action: "get",
    };
    dispatch(fetchProfileDetailsRequest(payload));
  }, []);

  useEffect(() => {
    if (profileDetails && profileDetails.fullname) {
      setName(profileDetails.fullname);
      setEmail(profileDetails.email);
      setMobile(profileDetails.mobile_no);
      setSignatureImagePreview(profileDetails.e_signature_img);
    }
  }, [profileDetails]);

  const handleEditClick = () => {
    setIsEditing(true);
    setOtpType("Profile");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") setName(value);
    if (name === "email") setEmail(value);
    if (name === "mobile") {
      if (/^\d*$/.test(value)) {
        setMobile(value);
      }
    }
  };

  const handleSaveClick = () => {
    const updatedFields = {};
    if (name !== profileDetails.fullname) {
      updatedFields.fullname = name;
    }
    if (email !== profileDetails.email) {
      updatedFields.email = email;
    }
    if (mobile !== profileDetails.mobile_no) {
      updatedFields.mobile_no = mobile;
    }
    if (Object.keys(updatedFields).length > 0) {
      let payload = { source: "Web", otp_type: "Profile" };
      setEditedFields(updatedFields);
      dispatch(sendOTPRequest(payload));
    } else {
      setShowAlert(true);
      setAlertTitle("Information!");
      setAlertType("info");
      setAlertMessage("No changes made");
      setIsEditing(false);
    }
  };

  const handleOtpSubmit = (otp) => {
    const payload = new FormData();
    payload.append("usr", profileDetails.email);
    payload.append("otp", otp);
    payload.append("otp_type", otpType);
    payload.append("source", "Web");

    if (otpType === "Profile") {
      if (signatureImage !== profileDetails.e_signature_img) {
        payload.append("e_signature_img", signatureImage);
      }
      Object.entries(editedFields).forEach(([key, value]) => {
        payload.append(key, value);
      });
      dispatch(verifyUserOTPRequest(payload));
    } else {
      payload.append("pwd", password);
      dispatch(verifyUserOTPRequest(payload));
    }
  };

  const handlePasswordChange = () => {
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    const strongRegex = new RegExp(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})"
    );
    if (!strongRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character."
      );
      return;
    }
    let payload = {
      source: "Web",
      otp_type: "FP",
      current_pwd: currentPassword,
    };
    dispatch(sendOTPRequest(payload));
  };

  const handleSignatureSubmit = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setSignatureImage(file);
      setOtpType("Profile");
      let payload = { source: "Web", otp_type: "Profile" };
      dispatch(sendOTPRequest(payload));
    }
  };

  return (
    <Layout>
      <Flex justify="space-between" align="center" mb="30px" mt="20px">
        <PrimaryHeading>User Profile</PrimaryHeading>
        {!isEditing && (
          <Box
            cursor="pointer"
            color="blue.600"
            fontSize="20px"
            onClick={handleEditClick}
          >
            <FaEdit />
          </Box>
        )}
      </Flex>
      <Box
        bgColor="white"
        p="20px"
        borderRadius="10px"
        mb="10px"
        id="user_details"
        position="relative"
      >
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <Flex flex="1">
            <PrimaryFormLabel mr="60px">Name:</PrimaryFormLabel>
            {!isEditing ? (
              <Text ml={2}>{name}</Text>
            ) : (
              <Input
                ml={2}
                name="name"
                value={name}
                onChange={handleInputChange}
              />
            )}
          </Flex>
          <Flex flex="1">
            <PrimaryFormLabel mr="60px">Email:</PrimaryFormLabel>
            {!isEditing ? (
              <Text ml={2}>{email}</Text>
            ) : (
              <Input
                ml={{ base: "2" }}
                name="email"
                value={email}
                onChange={handleInputChange}
                isReadOnly
              />
            )}
          </Flex>
          <Flex flex="1">
            <PrimaryFormLabel mr={{ base: "30px", md: "36px" }}>
              Mobile:
            </PrimaryFormLabel>
            {!isEditing ? (
              <Text ml={{ base: "35", md: "30" }}>{mobile}</Text>
            ) : (
              <Input
                ml={{ base: "8", md: "7" }}
                name="mobile"
                value={mobile}
                onChange={handleInputChange}
              />
            )}
          </Flex>

          <Flex flex="1">
            <PrimaryFormLabel mr={{ base: "20px", md: "20px" }}>
              Designation:
            </PrimaryFormLabel>
            {!isEditing ? (
              <Text>{profileDetails.designation}</Text>
            ) : (
              <Input
                name="designation"
                value={profileDetails.designation}
                onChange={handleInputChange}
                isReadOnly
              />
            )}
          </Flex>
          <Flex flex="1" direction="row">
            <PrimaryFormLabel mr={{ base: "20px", md: "40px" }}>
              Hospitals:
            </PrimaryFormLabel>
            {Array.isArray(profileDetails?.hospital) &&
              profileDetails?.hospital.length > 0 && (
                <Box
                  overflowY="auto"
                  maxHeight="300px"
                  border="1px solid #E2E8F0"
                  borderRadius="md"
                  fontSize="16px"
                >
                  <Table variant="simple">
                    <Thead position="sticky" top="0" bg="gray.100" zIndex="1">
                      <Tr>
                        <Th p="2" textAlign="left">
                          NO.
                        </Th>
                        <Th p="2" textAlign="left">
                          Hospital
                        </Th>
                        <Th p="2" textAlign="left">
                          Screen
                        </Th>
                        <Th p="2" textAlign="left">
                          Country
                        </Th>
                        <Th p="2" textAlign="left" whiteSpace="nowrap">
                          Access Type
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Array.isArray(profileDetails?.hospital) &&
                        profileDetails?.hospital.map((hospital, index) => (
                          <Tr key={index}>
                            <Td p="2">{index + 1}</Td>
                            <Td p="2" whiteSpace="nowrap">
                              {hospital.hospital}
                            </Td>
                            <Td p="2" whiteSpace="nowrap">
                              {hospital.screen}
                            </Td>
                            <Td p="2" whiteSpace="nowrap">
                              {hospital.country}
                            </Td>
                            <Td p="2">{hospital.access_type}</Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
          </Flex>
        </Grid>
        {!isEditing && (
          <Box textAlign="right">
            {profileDetails?.last_login && (
              <PrimaryText>Last Login: {profileDetails.last_login}</PrimaryText>
            )}
          </Box>
        )}
        {isEditing && (
          <Box mt="10px" textAlign="right">
            <PrimaryButton onClick={handleSaveClick} colorScheme="blue">
              Confirm Changes
            </PrimaryButton>
          </Box>
        )}
      </Box>
      <Box>
        <Box>
          <Flex alignItems="center" mt="20px">
            <PrimaryHeading>E-Signature</PrimaryHeading>
            <img
              src={CollapseIcon}
              width={"24px"}
              style={{
                cursor: "pointer",
                marginLeft: "10px",
                transition: "transform 0.3s ease",
                transform: isSignatureSectionOpen
                  ? "rotate(0)"
                  : "rotate(180deg)",
              }}
              onClick={toggleSignatureSection}
            />
          </Flex>
          <Collapse in={isSignatureSectionOpen} animateOpacity>
            <Box
              bgColor="white"
              p="20px"
              borderRadius="10px"
              mb="10px"
              mt="20px"
              id="user_details"
              position="relative"
            >
              {signatureImagePreview && (
                <Box mb="10px">
                  <img
                    src={signatureImagePreview}
                    alt="E-signature preview"
                    width="100px"
                    height="auto"
                  />
                </Box>
              )}
              <PrimaryButton
                mb="10px"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = ".svg,.jpg,.jpeg,.png";
                  input.click();
                  input.onchange = handleSignatureSubmit;
                }}
              >
                Upload E-signature
              </PrimaryButton>
            </Box>
          </Collapse>
        </Box>
        <Flex alignItems="center" mt="20px">
          <PrimaryHeading>Change Password</PrimaryHeading>
          <img
            src={CollapseIcon}
            width={"24px"}
            style={{
              cursor: "pointer",
              marginLeft: "10px",
              transition: "transform 0.3s ease",
              transform: isSectionOpen ? "rotate(0)" : "rotate(180deg)",
            }}
            onClick={toggleSection}
          />
        </Flex>
        <Collapse in={isSectionOpen} animateOpacity>
          <Box
            bgColor="white"
            mt="20px"
            p="20px"
            borderRadius="10px"
            mb="10px"
            id="user_details"
            position="relative"
          >
            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={4}
              mb="20px"
            >
              <PasswordField
                placeholder="Enter Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={passwordError}
              />
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <PasswordField
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordError}
              />
              <PasswordField
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={passwordError}
              />
            </Grid>
            {passwordError && (
              <Text mt={2} color="red.500">
                {passwordError}
              </Text>
            )}
            <Box mt="10px" textAlign="right">
              <PrimaryButton
                mt={4}
                colorScheme="blue"
                onClick={handlePasswordChange}
              >
                Update
              </PrimaryButton>
            </Box>
          </Box>
        </Collapse>
      </Box>
      <Alert
        isOpen={showAlert}
        title={alertTitle}
        onClose={() => {
          setShowAlert(false);
        }}
        body={alertMessage}
        type={alertType}
      />
      <Alert
        isOpen={isMessageOpen}
        title="Information!"
        onClose={() => {
          setMessageOpen(false);
          setIsEditing(false);
        }}
        body="No Changes made."
        type="info"
      />
      <Alert
        isOpen={incorrectPasswordAlert}
        title="Incorrect Password"
        onClose={() => setIncorrectPasswordAlert(false)}
        body="The current password you entered is incorrect. Please try again."
        type="error"
      />
      <OtpModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleOtpSubmit}
        user={profileDetails.email}
        otpType={otpType}
      />
    </Layout>
  );
};

export default UserProfile;
